import * as signalR from "@microsoft/signalr";
import { useEffect, useRef } from "react";

interface HeartbeatConfig {
    interval?: number;        // 心跳间隔（毫秒），默认30秒
    timeout?: number;         // 心跳超时（毫秒），默认5秒
    maxFailures?: number;     // 最大失败次数，默认3次
}

type UseHubConnectionOptions = {
    getAccessToken?: () => Promise<string>; // JWT令牌获取函数
};

const useHubConnection = (
    url: string,
    connections: (hubConnection: signalR.HubConnection) => void,
    onStart: () => Promise<void> | void = () => {},
    heartbeatConfig?: HeartbeatConfig,
    options?: UseHubConnectionOptions,
    enabled: boolean = true  // ✅ 新增参数：控制是否启用连接
) => {
    const connected = useRef(false);
    const hubConnectionRef = useRef<signalR.HubConnection | null>(null);
    const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const heartbeatFailureCountRef = useRef(0);
    const lastHeartbeatTimeRef = useRef<number>(Date.now());

    // 心跳配置
    const config: Required<HeartbeatConfig> = {
        interval: heartbeatConfig?.interval ?? 30000,      // 30秒
        timeout: heartbeatConfig?.timeout ?? 5000,         // 5秒
        maxFailures: heartbeatConfig?.maxFailures ?? 3,    // 3次失败后重连
    };

    /**
     * 启动心跳检测
     */
    const startHeartbeat = (hubConnection: signalR.HubConnection) => {
        console.log(`[Heartbeat] Starting heartbeat monitor (interval: ${config.interval}ms)`);
        
        heartbeatIntervalRef.current = setInterval(async () => {
            // 如果连接未建立，跳过心跳
            if (hubConnection.state !== signalR.HubConnectionState.Connected) {
                console.warn(`[Heartbeat] Skipping heartbeat check - connection state: ${hubConnection.state}`);
                return;
            }

            try {
                const heartbeatStartTime = Date.now();
                lastHeartbeatTimeRef.current = heartbeatStartTime;

                // 发送心跳信号，带超时
                const heartbeatPromise = hubConnection.invoke("Ping");
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error("Heartbeat timeout")), config.timeout)
                );

                await Promise.race([heartbeatPromise, timeoutPromise]);

                const latency = Date.now() - heartbeatStartTime;
                console.log(`[Heartbeat] ✓ Successful (latency: ${latency}ms)`);
                
                // 重置失败计数
                heartbeatFailureCountRef.current = 0;
            } catch (error) {
                heartbeatFailureCountRef.current++;
                console.warn(
                    `[Heartbeat] ✗ Failed (attempt ${heartbeatFailureCountRef.current}/${config.maxFailures}):`,
                    error instanceof Error ? error.message : error
                );

                // 如果失败次数过多，主动停止并重启连接
                if (heartbeatFailureCountRef.current >= config.maxFailures) {
                    console.error(
                        `[Heartbeat] Max failures reached (${config.maxFailures}), triggering reconnection...`
                    );
                    heartbeatFailureCountRef.current = 0;

                    try {
                        // 停止当前连接
                        if (hubConnection.state === signalR.HubConnectionState.Connected) {
                            await hubConnection.stop();
                        }
                    } catch (stopError) {
                        console.error("[Heartbeat] Error stopping connection:", stopError);
                    }

                    // 延迟后重启（让自动重连机制接管）
                    setTimeout(() => {
                        if (hubConnection.state === signalR.HubConnectionState.Disconnected) {
                            hubConnection.start().catch(err =>
                                console.error("[Heartbeat] Failed to restart connection:", err)
                            );
                        }
                    }, 1000);
                }
            }
        }, config.interval);
    };

    /**
     * 停止心跳检测
     */
    const stopHeartbeat = () => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
            console.log("[Heartbeat] Heartbeat monitor stopped");
        }
    };

    /**
     * 获取连接健康状态
     */
    const getConnectionHealth = (): {
        isHealthy: boolean;
        lastHeartbeat: number;
        failureCount: number;
    } => ({
        isHealthy: heartbeatFailureCountRef.current < config.maxFailures,
        lastHeartbeat: Date.now() - lastHeartbeatTimeRef.current,
        failureCount: heartbeatFailureCountRef.current,
    });

    useEffect(() => {
        // ✅ 新增：如果未启用，直接返回（保持 hooks 调用一致性）
        if (!enabled) {
            console.log("[SignalR] Connection disabled - skipping initialization");
            return;
        }

        const builder = new signalR.HubConnectionBuilder()
            .configureLogging(signalR.LogLevel.Information)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    if (retryContext.elapsedMilliseconds < 60000) {
                        return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
                    } else {
                        return null;
                    }
                }
            });

        // 配置认证令牌工厂
        if (options?.getAccessToken) {
            builder.withUrl(url, {
                accessTokenFactory: options.getAccessToken,
                skipNegotiation: false,
                transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
            });
            console.log("[SignalR] Using authenticated connection with JWT token factory");
        } else {
            builder.withUrl(url);
            console.warn("[SignalR] Connection established without authentication - getAccessToken not provided");
        }

        const hubConnection = builder.build();

        hubConnectionRef.current = hubConnection;

        connections(hubConnection);

        // 监听重新连接中
        hubConnection.onreconnecting(error => {
            console.warn(`[SignalR] Reconnecting due to error: ${error}`);
            connected.current = false;
            stopHeartbeat();  // 停止心跳
        });

        // 监听重新连接成功
        hubConnection.onreconnected(connectionId => {
            console.log(`[SignalR] Reconnected with connectionId: ${connectionId}`);
            connected.current = true;
            heartbeatFailureCountRef.current = 0;  // 重置失败计数
            startHeartbeat(hubConnection);  // 重启心跳
        });

        // 监听连接关闭
        hubConnection.onclose(error => {
            console.error(`[SignalR] Connection closed: ${error}`);
            connected.current = false;
            stopHeartbeat();  // 停止心跳
        });

        // 启动连接
        hubConnection.start()
            .then(() => {
                connected.current = true;
                console.log(`[SignalR] Connected successfully to ${url}`);
                
                // 延迟启动心跳，确保连接已建立
                setTimeout(() => {
                    startHeartbeat(hubConnection);
                }, 500);
                
                onStart();
            })
            .catch(error => {
                console.error(`[SignalR] Connection failed: ${error}`);
                connected.current = false;
            });

        return () => {
            stopHeartbeat();  // 清理心跳定时器
            
            if (hubConnectionRef.current && connected.current) {
                hubConnectionRef.current.stop()
                    .then(() => {
                        console.log("[SignalR] Connection stopped");
                    })
                    .catch(error => {
                        console.error(`[SignalR] Error stopping connection: ${error}`);
                    });
                connected.current = false;
            }
        };
    }, [url, options?.getAccessToken, enabled]);  // ✅ 添加 enabled 到依赖数组  // 只依赖于 url 和 getAccessToken 函数，而不是整个 options 对象

    return hubConnectionRef;
};

export default useHubConnection;
